import { useState } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { t } from 'i18next';

import { CustomDropZone } from '~/components/atoms/CustomDropZone';
import Accordion from '~/components/ui/Accordion/Accordion';
import AsyncSwitch from '~/components/ui/AsyncSwitch';
import { Button } from '~/components/ui/button';
import TextInput from '~/components/ui/TextInput';
import { DESKTOP_MIN_WIDTH, YouTubeURLRegexp } from '~/constants/common';
import { VIDEO_QR_ID_PARAM_NAME } from '~/constants/qr-code';
import useVideoQrData from '~/hooks/useVideoQrData';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { VideoQRSchema } from '~/pages/CreateQR/sub-components/videoTemplate/schema';
import { VideoItem } from '~/pages/CreateQR/sub-components/videoTemplate/VideoItem';
import { isMaxLength } from '~/utils/common';
import { getUrlZodValidation } from '~/utils/validation/url';

export default function VideoTemplateBasicInformation() {
  return (
    <CreateQRContentBlockLayout type="basicInfo">
      <VideoBlock />
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('builder:video.information.title')}
        details={<Information />}
        hideBottomBorder
      />
    </CreateQRContentBlockLayout>
  );
}

function VideoBlock() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [stopTrigger, setStopTrigger] = useState(false);
  const [dragTrigger, setDragTrigger] = useState(0);
  const [newVideoUrl, setNewVideoUrl] = useState({ value: '', error: '' });

  const { control } = useFormContext<VideoQRSchema>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'basicInfo.video.files',
    keyName: '_id',
  });

  const videoId = searchParams.get(VIDEO_QR_ID_PARAM_NAME);
  const { data: videoQrData } = useVideoQrData({ videoId });

  const handleAddVideo = () => {
    const { success, error } = getUrlZodValidation({ t })
      .refine((url) => YouTubeURLRegexp.test(url), { message: t('common:youTubeError') })
      .safeParse(newVideoUrl.value);

    if (success) {
      append({
        displayOrder: fields.length,
        description: '',
        sourceType: 'YOUTUBE',
        sourceUrl: newVideoUrl.value,
        videoFile: newVideoUrl.value,
      });

      setNewVideoUrl(() => ({
        value: '',
        error: '',
      }));
    } else {
      setNewVideoUrl((prevState) => ({
        ...prevState,
        error: error.errors[0].message,
      }));
    }
  };

  const handleDropVideo = (newVideos: any[]) => {
    newVideos.forEach((newVideo, index) => {
      append({
        videoFileId: newVideo.imageStorageId,
        sourceType: 'SELF_HOSTED',
        displayOrder: fields.length + index,
        videoFile: newVideo.previewLink,
      });
    });
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    move(result.source.index, result.destination.index);

    setDragTrigger((trigger) => trigger + 1);
    setStopTrigger(false);
  };

  return (
    <Accordion
      summary={t('builder:video.video.title')}
      variant="child"
      defaultExpanded
      details={
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '28px' }}>
            <TextInput
              value={newVideoUrl.value}
              onChange={(e) => setNewVideoUrl((prevState) => ({ ...prevState, value: e.target.value }))}
              sx={{ flex: 1 }}
              placeholder="https://www.youtube.com/watch..."
              error={!!newVideoUrl.error}
              helperText={newVideoUrl.error}
            />
            <Button
              onClick={handleAddVideo}
              sx={{ height: 'fit-content', marginBottom: '12px' }}
              size="xs"
              type="primary-outlined"
            >
              {t('builder:video.video.addVideo')}
            </Button>
          </Box>
          <CustomDropZone
            maxSize={250}
            maxSizeInKb={250000}
            acceptTypes="video/*"
            fileTypes="Video"
            existedLength={fields.length}
            onDrop={handleDropVideo}
          />
          {fields.length < 2 && (
            <Controller
              control={control}
              name="basicInfo.video.showVideoDirectly"
              render={({ field: { value, onChange, onBlur } }) => {
                return (
                  <AsyncSwitch
                    sx={{ marginTop: '12px' }}
                    checked={value}
                    onBlur={onBlur}
                    onChange={onChange}
                    label={t('builder:video.video.showDirectly')}
                  />
                );
              }}
            />
          )}
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided) => (
                <Box {...provided.droppableProps} sx={{ marginTop: '16px' }} ref={provided.innerRef}>
                  {fields.map((item, index, array) => {
                    return (
                      <VideoItem
                        key={item?.id}
                        remove={remove}
                        item={item}
                        index={index}
                        linkList={array}
                        stopTrigger={stopTrigger}
                        micrositeId={videoQrData?.id}
                        dragTrigger={dragTrigger}
                      />
                    );
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </>
      }
    />
  );
}

function Information() {
  const theme = useTheme();
  const { control, setValue, watch } = useFormContext<VideoQRSchema>();
  const [showCta, setShowCta] = useState(!!watch('basicInfo.information.buttonText'));
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const videos = watch('basicInfo.video.files');
  const showVideoDirectly = watch('basicInfo.video.showVideoDirectly');
  const buttonText = watch('basicInfo.information.buttonText');

  const handleResetCta = () => {
    setValue('basicInfo.information.buttonURL', '');
    setValue('basicInfo.information.buttonText', '');
    setShowCta(false);
  };

  const isShowVideoDirectly = showVideoDirectly && videos.length < 2;
  const isButtonTextMaxLengthError = !!isMaxLength(buttonText, 50);

  return (
    <>
      <Controller
        control={control}
        name="basicInfo.information.company"
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
          const maxLengthError = isMaxLength(value, 100); // Shouldn't be red

          return (
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!error?.message}
              helperText={error?.message || maxLengthError}
              fullWidth
              label={t('other:company')}
              placeholder={'E.g Worker John'}
              containerSx={{ marginBottom: '12px', marginTop: '12px' }}
              disabled={isShowVideoDirectly}
              inputProps={{ maxLength: 100 }}
            />
          );
        }}
      />
      <Controller
        control={control}
        name="basicInfo.information.videoTitle"
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
          const maxLengthError = isMaxLength(value, 100); // Shouldn't be red

          return (
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!error?.message}
              helperText={error?.message || maxLengthError}
              fullWidth
              label={t('builder:video.information.videoTitle')}
              placeholder="E.g. How to Build Your House"
              containerSx={{ marginBottom: '12px', marginTop: '12px' }}
              disabled={isShowVideoDirectly}
              inputProps={{ maxLength: 100 }}
            />
          );
        }}
      />
      <Controller
        control={control}
        name="basicInfo.information.description"
        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
          const maxLengthError = isMaxLength(value, 500); // Shouldn't be red

          return (
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!error?.message}
              helperText={error?.message || maxLengthError}
              fullWidth
              label={t('other:description')}
              placeholder="E.g. Gain essential skills and knowledge to confidently manage your home-building project, saving time and money while ensuring quality."
              containerSx={{ marginBottom: '12px', marginTop: '12px' }}
              disabled={isShowVideoDirectly}
              inputProps={{ maxLength: 500 }}
            />
          );
        }}
      />
      {showCta ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            alignItems: 'flex-end',
            marginBottom: isDesktop && isButtonTextMaxLengthError ? '12px' : undefined,
            [theme.breakpoints.up(DESKTOP_MIN_WIDTH)]: {
              flexDirection: 'row',
            },
          }}
        >
          <Box
            sx={{
              display: isDesktop ? 'contents' : 'flex',
              flex: 1,
              width: isDesktop ? 'initial' : '100%',
              marginTop: '12px',
              alignItems: 'flex-end',
            }}
          >
            <Controller
              control={control}
              name="basicInfo.information.buttonText"
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                const maxLengthError = isMaxLength(value, 50); // Shouldn't be red

                return (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!error?.message}
                    helperText={error?.message || maxLengthError}
                    sx={{ flex: 1 }}
                    fullWidth
                    label={t('builder:video.information.buttonText')}
                    disabled={isShowVideoDirectly}
                    inputProps={{ maxLength: 50 }}
                    absoluteHelperText={isDesktop}
                  />
                );
              }}
            />
            {!isDesktop && (
              <IconButton
                onClick={handleResetCta}
                sx={{ height: '40px', width: '40px' }}
                disabled={isShowVideoDirectly}
              >
                <ClearIcon />
              </IconButton>
            )}
          </Box>
          <Controller
            control={control}
            name="basicInfo.information.buttonURL"
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
              return (
                <TextInput
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!error?.message}
                  helperText={error?.message}
                  fullWidth
                  sx={{ flex: 1 }}
                  placeholder="https://www.example.com/"
                  label={t('builder:video.information.buttonUrl')}
                  disabled={isShowVideoDirectly}
                />
              );
            }}
          />
          {isDesktop && (
            <IconButton onClick={handleResetCta} sx={{ height: '40px', width: '40px' }} disabled={isShowVideoDirectly}>
              <ClearIcon />
            </IconButton>
          )}
        </Box>
      ) : (
        <Button onClick={() => setShowCta(true)} size="xs" type="primary-outlined" disabled={isShowVideoDirectly}>
          + {t('builder:video.information.addButton')}
        </Button>
      )}
    </>
  );
}
